import CollaborationCursor from '@tiptap/extension-collaboration-cursor'
import { TRANSITION_VARIANTS } from '@nextui-org/framer-transitions'

import { Listbox, ListboxSection, ListboxItem } from '@nextui-org/react'
import Tippy from '@tippyjs/react'
import * as awarenessProtocol from 'y-protocols/awareness.js'

import { EditorView } from 'prosemirror-view'

import { HocuspocusProvider } from '@hocuspocus/provider'

import { IndexeddbPersistence } from 'y-indexeddb'
import * as Y from 'yjs'

import { useSession, useUser } from '@supabase/auth-helpers-react'
import Collaboration from '@tiptap/extension-collaboration'
import { Editor, EditorContent } from '@tiptap/react'
import { useRouter } from 'next/router'
import { use, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createExtensions } from 'website/src/extensions'
import { DiffExtension } from 'website/src/extensions/diff'
import { startImageUpload } from 'website/src/extensions/upload-plugin'
import { atoms } from 'website/src/lib/atoms'
import { contentXPadding, githubProjectTypes } from 'website/src/lib/constants'
import {
    editorBasePath,
    getMarkdown,
    userToCursorUser,
} from 'website/src/lib/editor-utils'
import { TUTORIAL_PAGE_SLUG, env } from 'website/src/lib/env'
import { useRootProps } from 'website/src/lib/hooks'
import { startTutorial } from 'website/src/lib/tutorial'
import { debounce, sleep, yDocToJson } from 'website/src/lib/utils'
import { ySyncPluginKey } from 'y-prosemirror'
import { EditorBubbleMenu } from './BubbleMenu'
import { Prose } from './Prose'
import { useStore } from '@nanostores/react'
import { motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { NodeSelection, TextSelection } from 'prosemirror-state'

class Cache<T> {
    max = 7
    ttl = 1000 * 60 * 2
    cache = new Map<string, { value: T; lastUsed: number; active: boolean }>()
    dispose: Function
    constructor({ dispose }) {
        this.dispose = dispose
        setInterval(() => {
            const now = Date.now()
            for (let [k, v] of this.cache.entries()) {
                if (v.active) {
                    continue
                }
                if (now - v.lastUsed > this.ttl) {
                    this.delete(k)
                }
            }
        }, this.ttl + 10)
    }
    clear() {
        for (let k of this.cache.keys()) {
            this.delete(k)
        }
    }
    delete(k) {
        const value = this.cache.get(k)?.value
        if (value) {
            this.dispose(value, k)
        }
        return this.cache.delete(k)
    }
    makeAllInactive() {
        for (let k of this.cache.keys()) {
            this.cache.set(k, { ...this.cache.get(k)!, active: false })
        }
    }
    set(k, v: T) {
        if (this.cache.size >= this.max) {
            const oldest = Array.from(this.cache.entries()).reduce((a, b) =>
                a[1].lastUsed < b[1].lastUsed ? a : b,
            )

            this.delete(oldest[0])
        }
        this.makeAllInactive()
        this.cache.set(k, { value: v, lastUsed: Date.now(), active: true })
    }
    get(getK) {
        const value = this.cache.get(getK)?.value
        if (!value) {
            return
        }
        this.makeAllInactive()

        this.cache.set(getK, { value, lastUsed: Date.now(), active: true })

        return value
    }
}

let cache = new Cache<HocuspocusProvider>({
    dispose(provider: HocuspocusProvider) {
        // https://github.com/ueberdosis/hocuspocus/blob/4160dd7b93147787e6524dccd57e9012fc065d0d/packages/provider/src/HocuspocusProvider.ts#L450
        // if (provider.hasUnsyncedChanges) {
        //     provider.forceSync()
        // }

        console.log('disposing provider', provider.document?.clientID)
        provider?.destroy?.()
        provider?.document?.destroy()
        provider?.awareness?.destroy()
    },
})

export function TipTap({ className = '' }): any {
    const {
        pageId,

        documentBase64,
        websocketToken,
        pageSlug,
        siteId,
        pagesTree,
        isPR,
        projectType,
    } = useRootProps()
    const isLocalOnly =
        projectType === 'demo-project' ||
        githubProjectTypes.includes(projectType)
    const user = useUser()

    const session = useSession()

    const router = useRouter()

    useEffect(() => {
        const fn = (pathname) => {
            if (!editorBasePath(pathname)) {
                console.log(
                    'removing providers because navigating to',
                    pathname,
                )

                cache.clear()
            }
        }
        router.events.on('routeChangeStart', fn)
        return () => {
            router.events.off('routeChangeStart', fn)
        }
    }, [])

    const [editor, setEditor] = useState<Editor>()

    const firstRenderRef = useRef(true)

    useLayoutEffect(() => {
        if (firstRenderRef.current) {
            // don't recreate the editor the first time
            firstRenderRef.current = false
            if (editor) {
                return
            }
        }

        console.log(`pageId changed to ${pageId}`)

        const instance = createEditor({
            pageId,
            isLocalOnly,
            documentBase64,
            websocketToken,
            pageSlug,
            siteId,
            pagesTree,
            isPR,
            user,
        })?.editor

        setEditor(instance)

        return () => {
            instance?.destroy?.()
        }
    }, [pageId])

    if (!editor) {
        return null
    }
    return (
        <div
            style={{
                marginTop: 'var(--editorContentPaddingTop)',
                paddingLeft: contentXPadding,
                paddingRight: contentXPadding,
            }}
            className={className}
        >
            <Prose className='w-full'>
                <EditorContent className='' editor={editor} />
            </Prose>
            <EditorBubbleMenu editor={editor || undefined} />
            <LinkDropdown />
        </div>
    )
}

function LinkDropdown() {
    const ref = useStore(atoms.currentLink)
    const visible = !!ref.current
    const router = useRouter()
    if (!ref.current) {
        return null
    }
    return (
        <>
            {visible &&
                createPortal(
                    <motion.div
                        animate='enter'
                        exit='exit'
                        initial='exit'
                        variants={TRANSITION_VARIANTS.fade}
                        className='fixed inset-0 h-screen w-screen bg-overlay/50 backdrop-opacity-disabled'
                        onClick={() => {
                            atoms.currentLink.set({ current: null })
                        }}
                    />,
                    document.body,
                )}
            <Tippy
                reference={ref}
                // showOnCreate
                placement='right'
                // hideOnClick
                interactive
                visible={visible}
                appendTo={document.body}
                render={(attrs) => {
                    return (
                        <>
                            <div
                                className='z-50 relative bg-background rounded-lg px-2 py-1'
                                {...attrs}
                            >
                                <Listbox aria-label='Actions'>
                                    <ListboxItem
                                        onClick={() => {
                                            const p = ref.href || ''
                                            atoms.currentLink.set({
                                                current: null,
                                            })
                                            if (p.startsWith('/')) {
                                                router.push(p)
                                            } else {
                                                window.open(p, '_blank')
                                            }
                                        }}
                                        key='visit'
                                    >
                                        Visit link
                                    </ListboxItem>
                                    <ListboxItem
                                        onClick={async () => {
                                            const pos = ref.pos
                                            const { from, to } = ref

                                            atoms.currentLink.set({
                                                current: null,
                                                href: ref.href,
                                            })
                                            const editor = atoms.editor.get()!
                                            if (!pos || !from || !to) {
                                                console.error(`no pos found`)
                                                return
                                            }
                                            editor.view.focus()
                                            editor
                                                .chain()
                                                // .focus(from)
                                                // .setNodeSelection(pos)
                                                .setTextSelection({
                                                    from,
                                                    to,
                                                })
                                                .run()
                                            return
                                        }}
                                        key='edit'
                                    >
                                        Edit link
                                    </ListboxItem>
                                </Listbox>
                            </div>
                        </>
                    )
                }}
            />
        </>
    )
}

// Prevents a matchesNode error on hot reloading
// EditorView.prototype.updateState = function updateState(state) {
//     // @ts-ignore
//     if (!this.docView) return
//     // @ts-ignore
//     this.updateStateInner(state, this.state.plugins != state.plugins)
// }

function createEditor({
    pageId,
    isLocalOnly,
    documentBase64,
    websocketToken,
    pageSlug,
    siteId,
    pagesTree,
    isPR,
    user,
}) {
    console.log('creating editor for', pageSlug)

    atoms.pagesTree.set(pagesTree)
    if (!documentBase64) {
        return null
    }
    async function onDocumentSynced({ doc, awareness }) {
        const json = yDocToJson(doc)
        // console.log('json', json)
        const isEmpty = (() => {
            if (!json.content?.length) {
                return true
            }
            if (
                json.content.every((x) => {
                    return x.type === 'paragraph' && !x.content?.length
                })
            ) {
                return true
            }
            return false
        })()
        if (isEmpty && pageSlug === TUTORIAL_PAGE_SLUG) {
            await startTutorial({
                websocketToken,
                doc,
                awareness,
            })
        }
    }
    const buffer = Buffer.from(documentBase64, 'base64')
    const provider = (() => {
        const p = cache.get(pageId)
        if (p) {
            return p!
        }

        const doc = new Y.Doc()
        Y.applyUpdate(doc, buffer)

        if (isLocalOnly) {
            const awareness = new awarenessProtocol.Awareness(doc)
            const provider = {
                document: doc,
                awareness,
            }

            cache.set(pageId, provider as any)
            // onDocumentSynced({ doc, awareness })
            return provider
        }

        let url = env.NEXT_PUBLIC_HOCUSPOCUS_URL
        if (!url) {
            throw new Error('HOCUSPOCUS URL is not set')
        }

        const provider = new HocuspocusProvider({
            url: new URL(
                `/collaboration/${pageId}`,
                env.NEXT_PUBLIC_HOCUSPOCUS_URL,
            ).toString(),
            name: pageId,
            token: websocketToken,
            onAuthenticated() {},
            async onSynced() {
                await onDocumentSynced({
                    doc,
                    awareness: provider.awareness,
                })
            },
            // onAwarenessUpdate({ states }) {
            //     onAwarenessUpdate({ states, pagesTree, pageSlug })
            // },
            onAuthenticationFailed() {
                // TODO alert user about auth failure
                // supabase.auth.refreshSession()
            },

            document: doc,
        })

        cache.set(pageId, provider)
        return provider
    })()

    if (!provider) {
        throw new Error('Provider not found')
    }

    const extensions = createExtensions({ pageSlug })
    const doc = provider.document

    if (isPR) {
        let docWithoutAnonymousChanges = new Y.Doc()
        docWithoutAnonymousChanges.clientID = doc.clientID
        Y.applyUpdate(docWithoutAnonymousChanges, buffer)
        atoms.docWithoutAnonymousChanges.set(docWithoutAnonymousChanges)
        const onYUpdate = (update, origin) => {
            // ignore updates from this editor
            if (origin === ySyncPluginKey) {
                return
            }
            // ignore updates from this editor, from local storage
            if (origin === atoms.docPersistence.get()) {
                return
            }
            console.log('update', origin)
            Y.applyUpdate(docWithoutAnonymousChanges, update)
        }
        doc.on('update', onYUpdate)
        doc.on('destroy', () => {
            docWithoutAnonymousChanges.destroy()
        })
    }
    const persistence = new IndexeddbPersistence(
        pageId + (isPR ? '-pr' : ''),
        doc,
    )

    if (isLocalOnly) {
        persistence.on('synced', () => {
            onDocumentSynced({ doc, awareness: provider.awareness })
        })
    }
    persistence.on('cleared', () => {
        cache.clear()
    })

    atoms.docPersistence.set(persistence)

    if (!isPR) {
        atoms.docWithoutAnonymousChanges.set(null)
    }

    extensions.push(
        Collaboration.configure({
            document: doc,
        }),
    )
    if (isPR) {
        extensions.push(DiffExtension.configure({}))
    }

    // TODO maybe add cursor for tutorial thing

    const cursor = userToCursorUser(user, 'You')
    cursor.name ||= 'You'

    extensions.push(
        CollaborationCursor.extend({
            addOptions() {
                return {
                    ...this.parent?.(),
                    provider,
                    user: cursor,
                }
            },
        }).configure({
            provider: provider,
            user: cursor,
        }),
    )

    const onUpdate = debounce(({ editor }: { editor: Editor }) => {
        // console.log(JSON.stringify(editor.getJSON(), null, 2))
        if (isLocalOnly) {
            const markdown = getMarkdown(
                editor.state.doc,
                window.location.pathname,
            )
            localStorage.setItem(pageId, markdown)
        }
    }, 600)

    const editor = new Editor({
        extensions,

        editorProps: {
            attributes: {
                class: 'focus:outline-none pb-[100px] pb-[200px]',
                spellcheck: 'false',
            },

            handleDOMEvents: {
                keydown: (_view, event) => {
                    // prevent default event listeners from firing when slash command is active
                    if (['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
                        const slashCommand =
                            document.querySelector('#slash-command')
                        if (slashCommand) {
                            return true
                        }
                    }
                },
            },
            handlePaste: (view, event) => {
                if (
                    event.clipboardData &&
                    event.clipboardData.files &&
                    event.clipboardData.files[0]
                ) {
                    event.preventDefault()
                    const file = event.clipboardData.files[0]
                    const pos = view.state.selection.from

                    startImageUpload(file, view, pos)
                    return true
                }
                return false
            },
            handleDrop: (view, event, _slice, moved) => {
                if (
                    !moved &&
                    event.dataTransfer &&
                    event.dataTransfer.files &&
                    event.dataTransfer.files[0]
                ) {
                    event.preventDefault()
                    const file = event.dataTransfer.files[0]
                    const coordinates = view.posAtCoords({
                        left: event.clientX,
                        top: event.clientY,
                    })
                    // here we deduct 1 from the pos or else the image will create an extra node
                    startImageUpload(file, view, coordinates!.pos - 1)
                    return true
                }
                return false
            },
        },

        onUpdate,
        onCreate({ editor }) {
            console.log(`created editor`)
            atoms.editor.set(editor as any)
            editor.view.dom.focus()
            window.scrollTo(0, 0)
            // setTimeout(() => {
            //     editor.chain().focus(1, { scrollIntoView: false }).run()
            // }, 1000)
        },
        onDestroy() {},
    })
    atoms.document.set(provider?.document!)
    atoms.awareness.set(provider?.awareness!)

    provider?.awareness?.setLocalState({ user: cursor })

    // console.log(getAwarenessStates(provider.awareness, 'user'))
    // atoms.editor.set(editor as any)
    return {
        editor,
    }
}
